import { href } from 'react-router'
import { BrowseByCity } from '../components/browse-by-city'
import { SiteFooter } from '../components/footer'
import { HeroBackdrop } from '../components/hero-backdrop'
import { LocationPicker } from '../components/location-picker'
import { SearchForm } from '../components/search-form'
import { UsOnlyNotice } from '../components/us-only-notice'
// Wordmark served as cacheable SVG assets rather than inlined markup (VIT-17790):
// dark variant on the light mobile header, light variant on the dark hero.
import wordmarkDarkUrl from '../images/wordmark-dark.svg'
import wordmarkLightUrl from '../images/wordmark-light.svg'
import { formatGeoLabel, isOutsideUs } from '../routes/resource.geo'
import { pageMeta } from '../seo'
import { useGeo } from '../use-geo'
import type { Route } from './+types/home'

export const meta = (): Route.MetaDescriptors =>
  pageMeta({
    title: 'Find the right doctor | Vital Care Finder',
    description:
      'Vital builds a picture of your health, then matches doctors on cost, quality, and the actual health of their patients. Based on 10M+ real visits.',
    canonicalPath: href('/'),
  })

export const headers: Route.HeadersFunction = () => ({
  'Cache-Control': 'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400',
})

const Home = () => {
  const { geo, picked, settled: geoSettled } = useGeo()

  // Some ZIPs carry no city/state in the dataset, so a pick of one has no
  // "City, ST" label. Fall back to the bare ZIP — the same thing a ZIP results
  // page heads with — rather than to "Near you" (which loses the location) or to
  // CloudFront's city (which would name somewhere the search isn't centred on).
  const label = formatGeoLabel(geo) ?? picked?.postalCode ?? null

  // No `geoSettled` guard: isOutsideUs already returns false for unresolved geo,
  // so the notice can only appear once a source has produced a real country.
  //
  // No `picked` guard either: a pick carrying a ZIP or city is mapped to
  // `country: 'US'` by pickedToGeo, so choosing a US ZIP dismisses the notice on
  // its own. A GPS-only pick has no searchable location yet and falls through to
  // the CloudFront geo, where a non-US viewer should still be told.
  const outsideUs = isOutsideUs(geo)

  return (
    <>
      <main>
        <div className="px-2 pt-2 sm:px-4 sm:pt-4">
          <div className="sm:hidden pt-6 pb-8">
            <img
              src={wordmarkDarkUrl}
              alt="Vital Atlas"
              width={220}
              height={50}
              // Matches SiteHeader's mobile wordmark sizing (VIT-17951) so the logo is
              // the same size whether you land on home or on a content page.
              className="h-auto w-[168px] xs:w-[190px] [view-transition-name:wordmark]"
            />
          </div>
          <div className="overflow-hidden relative bg-black rounded-3xl px-4 py-4 sm:px-8 sm:py-10 text-white">
            <HeroBackdrop geo={geo} />
            <div className="absolute inset-0 bg-linear-to-r from-25% to-75% from-black to-transparent" />

            <div className="max-w-6xl mx-auto relative z-10 space-y-4 sm:space-y-10">
              <div className="md:w-2/3 md:text-balance space-y-6">
                <div className="hidden sm:block sm:h-10" />

                <div className="hidden sm:block">
                  <img
                    src={wordmarkLightUrl}
                    alt="Vital Atlas"
                    width={220}
                    height={50}
                    className="[view-transition-name:wordmark]"
                  />
                </div>
                <div className="hidden sm:block sm:h-10" />
                <h1 className="font-bold leading-[1.1] text-5xl sm:text-7xl text-balance font-display tracking-tight">
                  Find the right <br />
                  doctor <span className="text-primary-400">for you</span>
                </h1>

                {/* The row is always mounted so the hero doesn't shift when the
                  location arrives, but its contents stay invisible while the
                  geo fetch is in flight (also the SSR/pre-hydration state — no
                  mismatch). Once settled: the viewer's city, or "Near you"
                  when geo didn't resolve.

                  `inert` while unsettled because the label is now the location
                  picker's trigger button: an aria-hidden subtree must contain
                  nothing focusable (axe aria-hidden-focus), and a keyboard user
                  must not land on an invisible control. */}
                <div
                  aria-hidden={!geoSettled}
                  inert={!geoSettled}
                  className={`font-semibold text-xl sm:text-2xl flex items-center gap-2 ${geoSettled ? '' : 'invisible'}`}
                  data-testid="hero-location"
                >
                  {label != null ? (
                    <div>
                      in{' '}
                      <LocationPicker
                        className="text-primary-400 underline underline-offset-4"
                        data-testid="hero-location-picker"
                      >
                        {label}
                      </LocationPicker>
                    </div>
                  ) : (
                    <LocationPicker className="underline underline-offset-4" data-testid="hero-location-picker">
                      Near you
                    </LocationPicker>
                  )}
                </div>

                <p className="text-lg opacity-80" data-testid="hero-intro">
                  We find specialists &amp; primary care providers based on cost, quality, and{' '}
                  <em>actual health outcomes</em> for other patients like you.
                </p>
              </div>
              {/* A direct child of the hero stack, not wrapped with the form, so
                  the intro copy → notice → form gaps are all the stack's own
                  gap. */}
              {outsideUs && <UsOnlyNotice />}

              <SearchForm />

              <div className="hidden lg:block h-6" />
            </div>
          </div>
        </div>

        <BrowseByCity />
      </main>
      <SiteFooter />
    </>
  )
}

export default Home
